import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { enrollSelfInSession } from '../../lib/enrollSelfInSession';
import { emitSessionEvent } from '../../lib/sessionEvents';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useSessionStartWording } from '../../hooks/prioritization/useSessionStartWording';
import { usePresenceStore, useSessionPresence } from '../../stores/prioritization/presenceStore';
import { useSessionParticipantsStore } from '../../stores/prioritization/sessionParticipantsStore';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import type { PrioritizationSession } from '../../types/prioritization/session';
import { AnnouncementBanner } from './components/AnnouncementBanner';
import { ConnectionHealthBar } from './components/ConnectionHealthBar';
import { ItemSpotlightDrawer } from './components/ItemSpotlightDrawer';
import { LobbyView } from './components/LobbyView';
import { ParticipantShell } from './components/ParticipantShell';
import { SessionBrief } from './components/SessionBrief';
import { SessionChatDrawer } from './components/SessionChatDrawer';
import { SessionResultsView } from './components/SessionResultsView';
import { SessionRoomRouter } from './components/SessionRoomRouter';
import { SessionPausedOverlay } from './components/SessionPausedOverlay';
import { SubmissionTracker } from './components/SubmissionTracker';
import { WaitingRoom } from './components/WaitingRoom';
import { useSessionPhase, type ParticipantPhase } from './hooks/useSessionPhase';
import { useToolSessionStore } from '../../stores/prioritization/toolSessionStore';
import { useRoomReconnect } from '../../hooks/useRoomReconnect';
import { C } from './theme/participantTokens';
import { isSessionCompleted } from './utils/sessionHelpers';
import type { Item } from '../../types/prioritization';
import { getV3StepContext, type V3StepContext } from '../session-engine-v3/stepContext';
import { eventDesignerHref } from '../../lib/catchballSessionLink';
import { ReverseActionButton } from '../../components/reversals/ReverseActionButton';
import { leaveSession } from '../../lib/reversals/reversalsService';
import { namesHidden, useSessionRoomRules } from '../../lib/sessions/roomRules';

/**
 * P-LIVE-1: name the active V3 Event step (position, label, goal) inside the
 * room instead of an unframed tool run. Renders nothing for non-V3 sessions.
 */
function StepContextStrip({ ctx }: { ctx: V3StepContext }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
        padding: '7px 16px', borderBottom: `1px solid ${C.ruleBright}`,
        fontFamily: 'JetBrains Mono, monospace', fontSize: '0.58rem',
        letterSpacing: '0.1em', textTransform: 'uppercase',
      }}
    >
      <span style={{ color: C.textTer }}>
        Step {ctx.index} of {ctx.total}
      </span>
      <span style={{ color: C.textPri, fontWeight: 700 }}>{ctx.label}</span>
      {ctx.isMainEvent && (
        <span
          style={{
            padding: '1px 7px', borderRadius: 3,
            border: `1px solid ${C.mustard}`, color: C.mustard,
          }}
        >
          Main event
        </span>
      )}
      <span style={{ color: C.textDim, textTransform: 'none', letterSpacing: 0 }}>
        {ctx.goal ?? ctx.blurb}
      </span>
    </div>
  );
}

export type ParticipantShellMode = 'standalone' | 'prioritization';

interface ParticipantSessionFlowProps {
  sessionId: string;
  shell: ParticipantShellMode;
  backPath: string;
}

function phaseTitle(phase: ParticipantPhase): string {
  switch (phase) {
    case 'waiting': return 'Waiting Room';
    case 'lobby': return 'Lobby';
    case 'brief': return 'Session Brief';
    case 'room': return 'Session Room';
    case 'results': return 'Session Results';
    default: return 'Session';
  }
}

export function ParticipantSessionFlow({
  sessionId,
  shell,
  backPath,
}: ParticipantSessionFlowProps) {
  const navigate = useNavigate();

  const sessions = useSessionStore(s => s.sessions);
  const fetchSessionById = useSessionStore(s => s.fetchSessionById);
  const allItems = useItemsStore(s => s.items);
  const allParticipants = useSessionParticipantsStore(s => s.participants);
  const fetchParticipants = useSessionParticipantsStore(s => s.fetchParticipantsForSession);
  const userId = useUserStore(s => s.userId) ?? '';
  const userName = useUserStore(s => s.userName) ?? '';
  const updateActivity = usePresenceStore(s => s.updateActivity);

  const session = useMemo(
    () => sessions.find(s => s.id === sessionId) as PrioritizationSession | undefined,
    [sessions, sessionId],
  );
  // S-3: one answer for every screen in the flow.
  const hideNames = namesHidden(useSessionRoomRules(sessionId).rules, 'room');

  const { initialPhase } = useSessionPhase(session);
  const [phase, setPhase] = useState<ParticipantPhase>(initialPhase);
  const [chatOpen, setChatOpen] = useState(false);
  const [lookupDone, setLookupDone] = useState(false);
  // Enrolment can be refused by the server; show why and let the person retry.
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [enrollAttempt, setEnrollAttempt] = useState(0);

  // Live tool session ID for submission tracker
  const toolSessions = useToolSessionStore(s => s.toolSessions);
  const activeToolSessionId = useMemo(
    () => toolSessions.find(ts => ts.sessionId === sessionId)?.id ?? null,
    [toolSessions, sessionId]
  );
  const participantIds = useMemo(
    () => session?.participantIds ?? [],
    [session]
  );

  const participants = useMemo(
    () => allParticipants.filter(p => p.sessionId === sessionId),
    [allParticipants, sessionId],
  );

  // R4: once the session is open for ranking, show each item as it read then, not a later edit.
  const startWording = useSessionStartWording(session?.itemIds ?? [], session?.criteriaFinalizedAt);
  const sessionItems = useMemo(() => {
    if (!session) return [];
    // Standard sessions use item_ids; V3 sessions store items in mode_config.v3Config.items
    const standard = allItems
      .filter(i => session.itemIds?.includes(i.id))
      .map(i => ({ ...i, ...startWording(i.id, { title: i.title, description: i.description }) }));
    if (standard.length > 0) return standard;
    // Fall back to V3 config items (they may not be in allItems store yet)
    const v3ConfigItems: Array<{ id: string; title: string }> =
      ((session.modeConfig as Record<string, unknown>)?.v3Config as { items?: Array<{ id: string; title: string }> })?.items ?? [];
    return v3ConfigItems as unknown as Item[];
  }, [session, allItems, startWording]);

  useEffect(() => {
    if (!sessionId) return;
    setLookupDone(false);
    void Promise.all([fetchSessionById(sessionId), fetchParticipants(sessionId)])
      .finally(() => setLookupDone(true));
  }, [sessionId, fetchSessionById, fetchParticipants]);

  // Share-link join: presence alone does not grow the roster. Enroll into
  // session_participants + collecting tool_sessions.participant_ids (RPC 662).
  useEffect(() => {
    if (!sessionId || !userId) return;
    let cancelled = false;
    void (async () => {
      const result = await enrollSelfInSession(sessionId);
      if (cancelled) return;
      if (!result.success) {
        setEnrollError(result.error || 'The server did not add you to this session.');
        return;
      }
      setEnrollError(null);
      if (result.enrolled) {
        void emitSessionEvent(sessionId, userId, userName || 'Participant', 'participant_joined', {
          user_id: userId,
        });
      }
      await fetchParticipants(sessionId);
      await fetchSessionById(sessionId);
      await useToolSessionStore.getState().subscribeToSessionToolSessions(sessionId);
    })();
    return () => { cancelled = true; };
  }, [sessionId, userId, userName, fetchParticipants, fetchSessionById, enrollAttempt]);

  useSessionPresence(sessionId, userId, userName || 'Participant', 'participant');
  // S-7: a dropped connection picks itself back up, without a reload.
  useRoomReconnect(sessionId, { userId, userName: userName || 'Participant', role: 'participant' });

  useEffect(() => {
    if (session && isSessionCompleted(session)) {
      setPhase('results');
    }
  }, [session]);

  useEffect(() => {
    setPhase(initialPhase);
  }, [sessionId, initialPhase]);

  // Broadcast activity to presence channel whenever phase changes
  useEffect(() => {
    const activityMap: Record<typeof phase, import('../../stores/prioritization/presenceStore').PresenceActivity> = {
      waiting: 'waiting',
      lobby: 'waiting',
      brief: 'reading-brief',
      room: 'ranking',
      results: 'submitted',
    };
    void updateActivity(activityMap[phase] ?? 'waiting');
  }, [phase, updateActivity]);

  const handleBack = useCallback(() => {
    navigate(backPath);
  }, [navigate, backPath]);

  const handleStart = useCallback(() => {
    if (session && isSessionCompleted(session)) {
      setPhase('results');
      return;
    }
    setPhase('room');
  }, [session]);

  const sessionNav = (
    <>
      <button
        type="button"
        onClick={handleBack}
        style={{
          height: 28, padding: '0 12px', background: 'none', border: `1px solid ${C.ruleBright}`,
          borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: '0.58rem', color: C.textTer,
        }}
      >
        ← All Sessions
      </button>
      <button
        type="button"
        onClick={() => setChatOpen(o => !o)}
        title="Session chat"
        style={{
          height: 28, padding: '0 12px', background: chatOpen ? C.mustard : 'none',
          border: `1px solid ${chatOpen ? C.mustard : C.ruleBright}`,
          borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: '0.58rem',
          color: chatOpen ? C.bgRaised : C.textTer,
          transition: 'all 0.15s',
        }}
      >
        Chat
      </button>
      {session && participants.some((p) => p.userId === userId) && (
        <ReverseActionButton
          label="Leave session"
          confirmTitle="Leave session"
          explain="You will be removed from this session's roster. This is refused once you have submitted a response — ask the facilitator instead."
          run={(reason) => leaveSession(sessionId, userId, reason)}
          onDone={handleBack}
          variant="ghost"
          size="sm"
        />
      )}
    </>
  );

  if (!session) {
    const designerHref = eventDesignerHref({ resume: sessionId });
    const notFound = (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        {!lookupDone ? (
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', color: C.textDim }}>Loading session…</div>
        ) : (
          <>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', color: C.textDim }}>Session brief not found</div>
            <Link
              to={designerHref}
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: C.mustard }}
            >
              Open in Event Designer →
            </Link>
            <button type="button" onClick={handleBack} style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: C.textTer }}>
              ← Back
            </button>
          </>
        )}
      </div>
    );
    if (shell === 'standalone') {
      return (
        <ParticipantShell pageTitle="Session" showSidebar={false} sessionNav={sessionNav}>
          {notFound}
        </ParticipantShell>
      );
    }
    return <div className="pp min-h-[60vh] flex items-center justify-center">{notFound}</div>;
  }

  const stepCtx = getV3StepContext(session?.modeConfig);

  const content = (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* P-LIVE-1: V3 Event step context — which S1–S5 step this room is executing */}
      {(phase === 'brief' || phase === 'room') && stepCtx && <StepContextStrip ctx={stepCtx} />}
      {enrollError && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-2 text-sm text-[var(--coral)]"
        >
          <span>
            You have not been added to this session, so your input will not count yet: {enrollError}
          </span>
          <button
            type="button"
            onClick={() => { setEnrollError(null); setEnrollAttempt((n) => n + 1); }}
            className="shrink-0 border border-[var(--coral)] px-3 py-1 text-xs font-medium hover:opacity-80"
          >
            Try again
          </button>
        </div>
      )}
      {/* Phase 2: Announcement banner from facilitator broadcasts */}
      {phase === 'room' && <AnnouncementBanner sessionId={sessionId} />}
      {/* Submission tracker: visible during room and results phases */}
      {(phase === 'room' || phase === 'results') && (
        <SubmissionTracker toolSessionId={activeToolSessionId} participantIds={participantIds} hideNames={hideNames} />
      )}
      {phase === 'waiting' && (
        <WaitingRoom
          session={session}
          userId={userId}
          userName={userName}
          hideNames={hideNames}
          onAdvance={() => setPhase('brief')}
          onBack={handleBack}
        />
      )}
      {phase === 'lobby' && (
        <LobbyView
          session={session}
          onStartRanking={() => setPhase('brief')}
          onBack={handleBack}
          hideNames={hideNames}
        />
      )}
      {phase === 'brief' && (
        <SessionBrief
          session={session}
          items={sessionItems}
          participants={participants}
          userId={userId}
          hideNames={hideNames}
          onStart={handleStart}
          onBack={handleBack}
        />
      )}
      {phase === 'room' && (
        <SessionRoomRouter
          session={session}
          shell={shell}
          onBack={() => setPhase('brief')}
          onSubmitted={() => setPhase('brief')}
        />
      )}
      {phase === 'results' && (
        <SessionResultsView session={session} onBack={handleBack} />
      )}
    </div>
  );

  const chatDrawer = (
    <SessionChatDrawer
      sessionId={sessionId}
      isOpen={chatOpen}
      onClose={() => setChatOpen(false)}
      hideNames={hideNames}
    />
  );

  const healthBar = <ConnectionHealthBar sessionId={sessionId} />;
  // Phase 2 global overlays (mounted at flow level so they work across all phases)
  const phase2Overlays = (
    <>
      <SessionPausedOverlay sessionId={sessionId} />
      <ItemSpotlightDrawer sessionId={sessionId} />
    </>
  );

  if (shell === 'standalone') {
    return (
      <ParticipantShell pageTitle={phaseTitle(phase)} showSidebar={false} sessionNav={sessionNav}>
        {content}
        {chatDrawer}
        {healthBar}
        {phase2Overlays}
      </ParticipantShell>
    );
  }

  return (
    <div className="pp flex flex-col min-h-[calc(100vh-4rem)]" style={{ background: C.bg, color: C.textPri }}>
      {content}
      {/* Floating chat toggle for embedded mode */}
      <button
        type="button"
        onClick={() => setChatOpen(o => !o)}
        title="Session chat"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 88,
          width: 46, height: 46, borderRadius: '50%',
          background: chatOpen ? C.mustard : C.bgRaised,
          border: `1px solid ${chatOpen ? C.mustard : C.ruleBright}`,
          color: chatOpen ? C.bgRaised : C.textSec,
          cursor: 'pointer', fontSize: '1.05rem', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
          transition: 'all 0.15s',
        }}
      >
        💬
      </button>
      {chatDrawer}
      {healthBar}
      {phase2Overlays}
    </div>
  );
}
